import * as React from 'react';
import * as Yup from 'yup';
import propz from 'propz';
import { Field, Form, Formik, ErrorMessage } from 'formik';
import { Button } from '../../../components/Button/Button';
import { DatePicker as SitDatePicker } from '../../../components/DatePicker/DatePicker';
import { Autocomplete } from '../../../components/Autocomplete/Autocomplete';
import {
  getName,
  searchFunctionSchoolFormsPublic,
  searchFunctionSchoolHousesPublic
} from '../../../helpers/autocomplete/autocomplete';
import { GENDER_SERVER_TO_CLIENT_MAPPING, USER_GENDER } from '../../../consts/user';
import { SCHOOL_PERMISSION_REQUEST_FIELD_STATUS } from '../../../consts/school';
import { TextUtils } from '../../../helpers/utils/TextUtils';
import { School } from '../../../models/school';
import { RegistrationRequestConfirmation } from './RegistrationRequestConfirmation';

interface Props {
  onSubmit: (data, draftStudents?: any[]) => void;
  onBack?: (draftStudents: any[]) => void;
  initialStudents?: any[];
  school: any;
  isRequestReviewEnabled?: boolean;
  onConfirmRequest?: () => void;
  onBackFromReview?: () => void;
}
interface State {
  school: School;
}

function getValidationString(school, fieldName) {
  switch (school.additionalPermissionRequestFields[fieldName]) {
    case SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.REQUIRED:
      return Yup.string().required('Required');
    default:
      return Yup.string();
  }
}

export class ParentDetails extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
  }

  getSchoolForms = (text: string) => {
    const { school } = this.props;
    return searchFunctionSchoolFormsPublic(text, school.id);
  };

  getSchoolHouses = (text: string) => {
    const { school } = this.props;
    return searchFunctionSchoolHousesPublic(text, school.id);
  };

  render() {
    const { onSubmit, school } = this.props;

    const validationSchema = Yup.object().shape({
      students: Yup.array().of(
        Yup.object().shape({
          firstName: getValidationString(school, 'childFirstName'),
          lastName: getValidationString(school, 'childLastName'),
          gender: getValidationString(school, 'childGender'),
          birthday: getValidationString(school, 'childDateOfBirth'),
          formId: getValidationString(school, 'childForm'),
          houseId: getValidationString(school, 'childHouse'),
          oneOffCode: Yup.string()
        })
      )
    });

    const defaultStudents = [
      {
        firstName: '',
        lastName: '',
        gender: USER_GENDER.MALE,
        birthday: undefined,
        form: undefined,
        house: undefined,
        formId: '',
        houseId: '',
        oneOffCode: ''
      }
    ];
    const initialValues = {
      students:
        this.props.initialStudents && this.props.initialStudents.length > 0
          ? this.props.initialStudents.map(student => ({ ...student }))
          : defaultStudents,
      school: null
    };

    const isChildFirstName =
      school.additionalPermissionRequestFields.childFirstName !== SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN;
    const isChildLastName =
      school.additionalPermissionRequestFields.childLastName !== SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN;
    const isChildDateOfBirth =
      school.additionalPermissionRequestFields.childDateOfBirth !== SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN;
    const isChildGender =
      school.additionalPermissionRequestFields.childGender !== SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN;
    const isChildForm =
      school.additionalPermissionRequestFields.childForm !== SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN;
    const isChildHouse =
      school.additionalPermissionRequestFields.childHouse !== SCHOOL_PERMISSION_REQUEST_FIELD_STATUS.HIDDEN;

    return (
      <div className="eRegistrationParentDetails">
        {!this.props.isRequestReviewEnabled && (
          <div className="eSchoolSignupScreenHeader">
            <h1>Enter your child’s details</h1>
            <p>{school.name} asks for this so they can match your request to the right student record.</p>
          </div>
        )}

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={values => {
            const { students } = values;
            const dataToPost = students.map(student => {
              const { firstName, lastName, gender, birthday, formId, houseId, oneOffCode } = student;
              let studentData = {
                comment: `Request to be parent of [${firstName} ${lastName}]`,
                childGender: gender
              };

              if (!TextUtils.isEmpty(birthday)) {
                propz.set(studentData, ['childDateOfBirth'], birthday);
              }

              if (!TextUtils.isEmpty(formId)) {
                propz.set(studentData, ['childFormId'], formId);
              }

              if (!TextUtils.isEmpty(houseId)) {
                propz.set(studentData, ['childHouseId'], houseId);
              }

              if (!TextUtils.isEmpty(oneOffCode)) {
                propz.set(studentData, ['details', 'oneOffCode'], oneOffCode);
              }

              return studentData;
            });

            onSubmit(dataToPost, values.students);
          }}
        >
          {({ values, setFieldValue }) =>
            this.props.isRequestReviewEnabled ? (
              <RegistrationRequestConfirmation
                schoolName={school.name || 'your school'}
                roleLabel="Parent or guardian"
                title="Check your parent request"
                lines={[
                  <>
                    The school will use the details you entered to match{' '}
                    {values.students.length === 1 ? 'your child' : `${values.students.length} children`} by hand.
                  </>,
                  <>Nothing in a child’s account becomes visible until the school approves the match.</>,
                  <>We’ll let you know as soon as the request is accepted or declined.</>
                ]}
                onConfirm={this.props.onConfirmRequest}
                onBack={this.props.onBackFromReview}
              />
            ) : (
              <Form>
                {values.students.map((student, index) => (
                <div key={`student_${index}`} className="eRegistrationStudentBlock">
                  <div className="eRegistrationStudentBlockHeader">
                    <div className="eRegistrationStudentBlockTitle">Child {index + 1}</div>
                    {values.students.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          const nextStudents = values.students.filter((item, i) => i !== index);
                          setFieldValue('students', nextStudents);
                        }}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="eRegistrationChildGrid">
                    {isChildFirstName && (
                      <div className="form-group">
                        <label>First name</label>
                        <Field name={`students.${index}.firstName`} className="form-control mb-3" />
                        <ErrorMessage
                          component="div"
                          className="alert alert-danger mt-3"
                          name={`students.${index}.firstName`}
                        />
                      </div>
                    )}

                    {isChildLastName && (
                      <div className="form-group">
                        <label>Last name</label>
                        <Field name={`students.${index}.lastName`} className="form-control mb-3" />
                        <ErrorMessage
                          component="div"
                          className="alert alert-danger mt-3"
                          name={`students.${index}.lastName`}
                        />
                      </div>
                    )}

                    {isChildDateOfBirth && (
                      <div className="form-group">
                        <Field name={`students.${index}.birthday`}>
                          {() => (
                            <SitDatePicker
                              label="Date of birth"
                              mode="single"
                              value={student.birthday || null}
                              onChange={birthday => {
                                setFieldValue(`students.${index}.birthday`, birthday || '');
                              }}
                              placeholder="Select date of birth"
                              format="DD MMM YYYY"
                              max={new Date()}
                              centered
                              className="eRegistrationBirthdayPicker"
                            />
                          )}
                        </Field>
                        <ErrorMessage
                          component="div"
                          className="alert alert-danger mt-3"
                          name={`students.${index}.birthday`}
                        />
                      </div>
                    )}

                    {isChildGender && (
                      <div className="form-group">
                        <label>Gender</label>
                        <Field component="select" name={`students.${index}.gender`} className="form-control mb-3">
                          <option value={USER_GENDER.MALE}>{GENDER_SERVER_TO_CLIENT_MAPPING.MALE}</option>
                          <option value={USER_GENDER.FEMALE}>{GENDER_SERVER_TO_CLIENT_MAPPING.FEMALE}</option>
                        </Field>
                        <ErrorMessage
                          component="div"
                          className="alert alert-danger mt-3"
                          name={`students.${index}.gender`}
                        />
                      </div>
                    )}

                    {isChildForm && (
                      <div className="form-group">
                        <label>Form</label>
                        <Field name={`students.${index}.form`}>
                          {() => (
                            <Autocomplete
                              searchFunction={this.getSchoolForms}
                              getElementTitle={getName}
                              customClass="mFullWidth mb-3"
                              defaultItem={student.form}
                              showAllOnFocus
                              emptyMessage="No matching forms"
                              onSelect={form => {
                                setFieldValue(`students.${index}.formId`, form.id);
                                setFieldValue(`students.${index}.form`, form);
                              }}
                            />
                          )}
                        </Field>
                        <ErrorMessage
                          component="div"
                          className="alert alert-danger mt-3"
                          name={`students.${index}.formId`}
                        />
                      </div>
                    )}

                    {isChildHouse && (
                      <div className="form-group">
                        <label>House</label>
                        <Field name={`students.${index}.house`}>
                          {() => (
                            <Autocomplete
                              searchFunction={this.getSchoolHouses}
                              getElementTitle={getName}
                              customClass="mFullWidth mb-3"
                              defaultItem={student.house}
                              showAllOnFocus
                              emptyMessage="No matching houses"
                              onSelect={house => {
                                setFieldValue(`students.${index}.houseId`, house.id);
                                setFieldValue(`students.${index}.house`, house);
                              }}
                            />
                          )}
                        </Field>
                        <ErrorMessage
                          component="div"
                          className="alert alert-danger mt-3"
                          name={`students.${index}.houseId`}
                        />
                      </div>
                    )}

                    {school.isOneOffCodeOnRegisterEnabled && (
                      <div className="form-group">
                        <label>If the school provided you with a unique code please enter it here</label>
                        <Field name={`students.${index}.oneOffCode`} className="form-control mb-3" />
                        <ErrorMessage
                          component="div"
                          className="alert alert-danger mt-3"
                          name={`students.${index}.oneOffCode`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}

                <div className="eRegistrationActionsRow">
                  {this.props.onBack && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => this.props.onBack(values.students)}
                    >
                      Back
                    </button>
                  )}
                  <Button
                    text="Add another child"
                    customClass="btn-secondary eRegistrationAddChildButton"
                    onClick={() => {
                      const students = [
                        ...values.students,
                        {
                          firstName: '',
                          lastName: '',
                          gender: USER_GENDER.MALE,
                          birthday: undefined,
                          form: undefined,
                          house: undefined,
                          formId: '',
                          houseId: '',
                          oneOffCode: ''
                        }
                      ];
                      setFieldValue('students', students);
                    }}
                  />

                  <button type="submit" className="btn btn-primary">
                    Review request
                  </button>
                </div>
              </Form>
            )
          }
        </Formik>
      </div>
    );
  }
}
